import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
    Check, 
    Crown, 
    Star, 
    Sparkles, 
    Loader2,
    ArrowRight,
    CheckCircle,
    XCircle,
    Gift,
    Clock,
    Zap,
    Shield,
    CreditCard,
    Bell,
    BellOff,
    Mail
} from 'lucide-react';
import { toast } from 'sonner';

export const SubscriptionPage = () => {
    const { user, isPremium, refreshUser } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [checkoutLoading, setCheckoutLoading] = useState(null);
    const [paymentStatus, setPaymentStatus] = useState(null);
    const [trialStatus, setTrialStatus] = useState(null);
    const [emailNotifications, setEmailNotifications] = useState(true);
    const [prefsLoading, setPrefsLoading] = useState(false);

    const [hasCheckedPayment, setHasCheckedPayment] = useState(false);

    useEffect(() => {
        loadPlans();
        if (user) {
            checkTrialStatus();
            loadPreferences();
        }
    }, [user]);

    // Separate effect for payment status check - runs only once
    useEffect(() => {
        const sessionId = searchParams.get('session_id');
        if (sessionId && user && !hasCheckedPayment) {
            setHasCheckedPayment(true);
            checkPaymentStatus();
        }
    }, [user, searchParams, hasCheckedPayment]);

    const loadPlans = async () => {
        try {
            const response = await api.getPlans();
            setPlans(response.data);
        } catch (error) {
            console.error('Error loading plans:', error);
        } finally {
            setLoading(false);
        }
    };

    const checkTrialStatus = async () => {
        try {
            const response = await api.getTrialStatus();
            setTrialStatus(response.data);
        } catch (error) {
            console.error('Error checking trial status:', error);
        }
    };

    const loadPreferences = async () => {
        try {
            const response = await api.getUserPreferences();
            setEmailNotifications(response.data.email_notifications);
        } catch (error) {
            console.error('Error loading preferences:', error);
        }
    };

    const toggleEmailNotifications = async () => {
        setPrefsLoading(true);
        try {
            const newValue = !emailNotifications;
            await api.updateUserPreferences({ email_notifications: newValue });
            setEmailNotifications(newValue);
            toast.success(newValue 
                ? 'Notifications par email activées' 
                : 'Notifications par email désactivées'
            );
        } catch (error) {
            toast.error('Erreur lors de la mise à jour des préférences');
        } finally {
            setPrefsLoading(false);
        }
    };

    const checkPaymentStatus = async () => {
        const sessionId = searchParams.get('session_id');
        if (sessionId && user) {
            setPaymentStatus('checking');
            try {
                let attempts = 0;
                const maxAttempts = 5;
                const pollInterval = 2000;
                
                const poll = async () => {
                    const response = await api.getCheckoutStatus(sessionId);
                    
                    if (response.data.payment_status === 'paid') {
                        setPaymentStatus('success');
                        await refreshUser();
                        toast.success('Bienvenue! Votre premier mois est GRATUIT!');
                        // Clear the session_id from URL to prevent re-checking
                        window.history.replaceState({}, '', '/subscription');
                        return;
                    } else if (response.data.status === 'expired') {
                        setPaymentStatus('failed');
                        toast.error('La session de paiement a expiré');
                        return;
                    }
                    
                    attempts++;
                    if (attempts < maxAttempts) {
                        setTimeout(poll, pollInterval);
                    } else {
                        setPaymentStatus('pending');
                    }
                };
                
                await poll();
            } catch (error) {
                console.error('Error checking payment status:', error);
                setPaymentStatus('failed');
            }
        }
    };

    const handleSubscribe = async (planId) => {
        if (!user) {
            navigate('/register');
            return;
        }
        
        if (planId === 'free') return;
        
        setCheckoutLoading(planId);
        try {
            const originUrl = window.location.origin;
            const response = await api.createCheckout(planId, originUrl);
            window.location.href = response.data.checkout_url;
        } catch (error) {
            console.error('Error creating checkout:', error);
            toast.error('Erreur lors de la création du paiement');
        } finally {
            setCheckoutLoading(null);
        }
    };

    const premiumFeatures = [
        'Pack Hebdomadaire complet chaque semaine',
        '5 soupers planifiés avec recettes',
        'Liste d\'épicerie prête à imprimer',
        'Rabais de la semaine intégrés',
        'Suggestions IA personnalisées',
        'Astuces de congélation et recette secours'
    ];

    // User is on trial period
    const isOnTrial = trialStatus?.is_in_trial;
    
    if (isPremium && !isOnTrial) {
        return (
            <div className="min-h-screen py-12 md:py-24 relative overflow-hidden">
                <div className="blob-green top-20 -right-20 opacity-30" />
                <div className="blob-purple bottom-20 -left-20 opacity-30" />
                
                <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
                    <div className="w-24 h-24 bg-gradient-cameleon rounded-full flex items-center justify-center mx-auto mb-6 border-4 border-foreground shadow-[6px_6px_0px_0px_#1e293b]">
                        <Crown className="w-12 h-12 text-white" />
                    </div>
                    <h1 className="font-heading text-5xl md:text-6xl font-black text-foreground mb-4">
                        Vous êtes <span className="text-gradient-cameleon">Premium</span>!
                    </h1>
                    <p className="text-muted-foreground text-xl mb-10">
                        Merci de votre soutien! Vous avez accès à toutes les fonctionnalités exclusives.
                    </p>
                    
                    <div className="card-green p-8 text-left">
                        <h3 className="font-heading font-bold text-xl mb-6 text-center">Vos avantages Premium</h3>
                        <ul className="space-y-4">
                            {premiumFeatures.map((feature, index) => (
                                <li key={index} className="flex items-center gap-3">
                                    <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                                        <Check className="w-5 h-5 text-white" />
                                    </div>
                                    <span className="text-foreground font-medium">{feature}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                    
                    {/* Notification Preferences */}
                    <div className="card-purple p-6 mt-6">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                {emailNotifications ? (
                                    <div className="w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center">
                                        <Bell className="w-5 h-5 text-white" />
                                    </div>
                                ) : (
                                    <div className="w-10 h-10 bg-gray-400 rounded-full flex items-center justify-center">
                                        <BellOff className="w-5 h-5 text-white" />
                                    </div>
                                )}
                                <div>
                                    <h4 className="font-heading font-bold">Notifications par email</h4>
                                    <p className="text-sm text-muted-foreground">
                                        Recevoir un email lors d'un nouveau pack hebdomadaire
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={toggleEmailNotifications}
                                disabled={prefsLoading}
                                className={`relative w-14 h-8 rounded-full transition-colors ${
                                    emailNotifications ? 'bg-purple-500' : 'bg-gray-300'
                                }`}
                                data-testid="notification-toggle"
                            >
                                <span className={`absolute top-1 w-6 h-6 bg-white rounded-full shadow transition-transform ${
                                    emailNotifications ? 'left-7' : 'left-1'
                                }`} />
                                {prefsLoading && (
                                    <Loader2 className="w-4 h-4 animate-spin absolute top-2 left-5 text-white" />
                                )}
                            </button>
                        </div>
                    </div>
                    
                    <Button
                        onClick={() => navigate('/weekly-pack')}
                        className="mt-10 btn-pop bg-primary text-white text-lg px-8 py-6"
                    >
                        Voir le Pack Hebdomadaire
                        <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                </div>
            </div>
        );
    }

    // User is in trial period
    if (isOnTrial && trialStatus?.is_active) {
        return (
            <div className="min-h-screen py-12 md:py-24 relative overflow-hidden">
                <div className="blob-green top-20 -right-20 opacity-30" />
                <div className="blob-orange bottom-20 -left-20 opacity-30" />
                
                <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
                    <div className="w-24 h-24 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6 border-4 border-foreground shadow-[6px_6px_0px_0px_#1e293b] animate-bounce-subtle">
                        <Gift className="w-12 h-12 text-white" />
                    </div>
                    <Badge className="bg-green-500 text-white px-4 py-2 text-base font-bold border-2 border-foreground shadow-pop mb-4">
                        <Clock className="w-4 h-4 mr-2" />
                        {trialStatus.days_remaining} jours restants sur votre mois gratuit
                    </Badge>
                    <h1 className="font-heading text-5xl md:text-6xl font-black text-foreground mb-4">
                        Mois Gratuit <span className="text-green-500">Actif</span>!
                    </h1>
                    <p className="text-muted-foreground text-xl mb-10">
                        Profitez de toutes les fonctionnalités Premium pendant votre premier mois gratuit.
                    </p>
                    
                    <div className="card-green p-8 text-left mb-8">
                        <h3 className="font-heading font-bold text-xl mb-6 text-center">Vos avantages inclus</h3>
                        <ul className="space-y-4">
                            {premiumFeatures.map((feature, index) => (
                                <li key={index} className="flex items-center gap-3">
                                    <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                                        <Check className="w-5 h-5 text-white" />
                                    </div>
                                    <span className="text-foreground font-medium">{feature}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                    
                    {/* Notification Preferences */}
                    <div className="card-purple p-6 mb-8">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                {emailNotifications ? (
                                    <div className="w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center">
                                        <Bell className="w-5 h-5 text-white" />
                                    </div>
                                ) : (
                                    <div className="w-10 h-10 bg-gray-400 rounded-full flex items-center justify-center">
                                        <BellOff className="w-5 h-5 text-white" />
                                    </div>
                                )}
                                <div className="text-left">
                                    <h4 className="font-heading font-bold">Notifications par email</h4>
                                    <p className="text-sm text-muted-foreground">
                                        Recevoir un email lors d'un nouveau pack
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={toggleEmailNotifications}
                                disabled={prefsLoading}
                                className={`relative w-14 h-8 rounded-full transition-colors ${
                                    emailNotifications ? 'bg-purple-500' : 'bg-gray-300'
                                }`}
                            >
                                <span className={`absolute top-1 w-6 h-6 bg-white rounded-full shadow transition-transform ${
                                    emailNotifications ? 'left-7' : 'left-1'
                                }`} />
                                {prefsLoading && (
                                    <Loader2 className="w-4 h-4 animate-spin absolute top-2 left-5 text-white" />
                                )}
                            </button>
                        </div>
                    </div>
                    
                    <p className="text-muted-foreground mb-6">
                        Votre période gratuite se termine le <strong>{new Date(trialStatus.trial_ends_at).toLocaleDateString('fr-CA')}</strong>
                    </p>
                    
                    <Button
                        onClick={() => navigate('/weekly-pack')}
                        className="btn-pop bg-primary text-white"
                    >
                        Voir le Pack Hebdomadaire
                        <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                </div>
            </div>
        );
    }

    if (paymentStatus === 'success') {
        return (
            <div className="min-h-screen flex items-center justify-center py-12">
                <div className="text-center">
                    <div className="w-24 h-24 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6 border-4 border-foreground shadow-[6px_6px_0px_0px_#1e293b]">
                        <CheckCircle className="w-12 h-12 text-white" />
                    </div>
                    <h1 className="font-heading text-4xl font-black text-foreground mb-4">
                        Bienvenue!
                    </h1>
                    <p className="text-muted-foreground text-lg mb-2">
                        Votre compte Premium est activé.
                    </p>
                    <p className="text-green-600 font-bold text-xl mb-8">
                        Votre premier mois est GRATUIT!
                    </p>
                    <Button
                        onClick={() => navigate('/weekly-pack')}
                        className="btn-pop bg-primary text-white"
                    >
                        Découvrir le Pack Premium
                    </Button>
                </div>
            </div>
        );
    }

    if (paymentStatus === 'failed') {
        return (
            <div className="min-h-screen flex items-center justify-center py-12">
                <div className="text-center">
                    <div className="w-24 h-24 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-6 border-4 border-foreground shadow-[6px_6px_0px_0px_#1e293b]">
                        <XCircle className="w-12 h-12 text-white" />
                    </div>
                    <h1 className="font-heading text-4xl font-black text-foreground mb-4">
                        Paiement échoué
                    </h1>
                    <p className="text-muted-foreground text-lg mb-8">
                        Le paiement n&apos;a pas pu être traité. Veuillez réessayer.
                    </p>
                    <Button
                        onClick={() => navigate('/subscription')}
                        className="btn-pop bg-primary text-white"
                    >
                        Réessayer
                    </Button>
                </div>
            </div>
        );
    }

    if (paymentStatus === 'checking') {
        return (
            <div className="min-h-screen flex items-center justify-center py-12">
                <div className="text-center">
                    <Loader2 className="w-16 h-16 animate-spin text-primary mx-auto mb-6" />
                    <h1 className="font-heading text-2xl font-bold text-foreground mb-2">
                        Vérification du paiement...
                    </h1>
                    <p className="text-muted-foreground">
                        Veuillez patienter quelques instants
                    </p>
                </div>
            </div>
        );
    }

    // Plan card colors
    const getPlanStyle = (plan) => {
        if (plan.id === 'free') {
            return 'border-gray-300 shadow-[6px_6px_0px_0px_#d1d5db]';
        }
        if (plan.is_popular) {
            return 'border-green-500 shadow-[8px_8px_0px_0px_hsl(150,100%,40%)]';
        }
        return 'border-purple-500 shadow-[6px_6px_0px_0px_hsl(270,80%,50%)]';
    };

    return (
        <div className="min-h-screen py-12 md:py-24 relative overflow-hidden" data-testid="subscription-page">
            {/* Decorative blobs */}
            <div className="blob-green top-20 -right-40 opacity-20" />
            <div className="blob-purple bottom-40 -left-40 opacity-20" />
            <div className="blob-orange top-1/2 right-0 opacity-15" />
            
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                {/* Header */}
                <div className="text-center mb-12">
                    <span className="inline-flex items-center gap-2 px-5 py-2.5 bg-white rounded-full border-2 border-foreground shadow-pop font-heading font-bold text-sm mb-6">
                        <Sparkles className="w-4 h-4 text-purple-500" />
                        Offres Premium
                    </span>
                    <h1 className="font-heading text-5xl md:text-7xl font-black text-foreground mb-4">
                        Passez au
                        <br />
                        <span className="text-gradient-cameleon">niveau supérieur</span>
                    </h1>
                    <p className="text-muted-foreground text-xl max-w-2xl mx-auto">
                        Débloquez toutes les recettes exclusives, les suggestions IA et bien plus encore!
                    </p>
                </div>

                {/* Trial Banner - First month free with card */}
                <div className="max-w-3xl mx-auto mb-16 bg-gradient-cameleon rounded-3xl p-8 md:p-10 text-center border-4 border-foreground shadow-[8px_8px_0px_0px_#1e293b] relative overflow-hidden">
                    {/* Decorative elements */}
                    <div className="absolute top-4 left-4 w-16 h-16 border-4 border-white/20 rounded-full" />
                    <div className="absolute bottom-4 right-4 w-12 h-12 border-4 border-white/20 rounded-2xl rotate-12" />
                    
                    <div className="relative z-10">
                        <div className="w-20 h-20 bg-white/20 backdrop-blur rounded-full flex items-center justify-center mx-auto mb-6">
                            <Gift className="w-10 h-10 text-white" />
                        </div>
                        <h2 className="font-heading text-4xl md:text-5xl font-black text-white mb-4">
                            Premier mois <span className="text-yellow-300">GRATUIT</span>!
                        </h2>
                        <p className="text-white/90 text-lg mb-6 max-w-xl mx-auto">
                            Essayez Premium pendant 30 jours complets sans frais.
                            <br />
                            Annulez à tout moment avant la fin du mois d&apos;essai.
                        </p>
                        <div className="flex items-center justify-center gap-2 bg-white/20 backdrop-blur rounded-full px-5 py-3 w-fit mx-auto">
                            <CreditCard className="w-5 h-5 text-white" />
                            <span className="text-white font-bold">Carte requise pour valider l&apos;essai</span>
                        </div>
                    </div>
                </div>

                {/* Plans */}
                {loading ? (
                    <div className="flex justify-center">
                        <Loader2 className="w-12 h-12 animate-spin text-primary" />
                    </div>
                ) : (
                    <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
                        {plans.map((plan) => (
                            <div 
                                key={plan.id}
                                className={`relative bg-white rounded-3xl border-2 p-6 transition-all hover:-translate-y-2 ${getPlanStyle(plan)} ${plan.is_popular ? 'md:scale-105 z-10' : ''}`}
                                data-testid={`plan-${plan.id}`}
                            >
                                {plan.is_popular && (
                                    <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                                        <span className="bg-green-500 text-white px-5 py-1.5 rounded-full text-sm font-bold border-2 border-foreground shadow-pop">
                                            Populaire
                                        </span>
                                    </div>
                                )}
                                
                                {plan.has_trial && (
                                    <div className="absolute -top-3 right-4">
                                        <Badge className="bg-orange-500 text-white border-2 border-foreground shadow-pop">
                                            <Gift className="w-3 h-3 mr-1" />
                                            1 mois gratuit
                                        </Badge>
                                    </div>
                                )}
                                
                                <div className="text-center pt-6 pb-4">
                                    <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 border-2 border-foreground ${
                                        plan.id === 'free' 
                                            ? 'bg-gray-100' 
                                            : plan.is_popular 
                                                ? 'bg-green-500' 
                                                : 'bg-purple-500'
                                    }`}>
                                        {plan.id === 'free' ? (
                                            <Star className="w-8 h-8 text-gray-500" />
                                        ) : plan.is_popular ? (
                                            <Zap className="w-8 h-8 text-white" />
                                        ) : (
                                            <Crown className="w-8 h-8 text-white" />
                                        )}
                                    </div>
                                    <h3 className="font-heading text-2xl font-black">{plan.name}</h3>
                                    <div className="mt-4">
                                        <span className="font-heading text-5xl font-black text-foreground">
                                            {plan.price === 0 ? 'Gratuit' : `${plan.price.toFixed(2)}$`}
                                        </span>
                                        {plan.price > 0 && (
                                            <span className="text-muted-foreground text-lg font-medium">
                                                {plan.id === 'premium_weekly' ? '/sem' : '/mois'}
                                            </span>
                                        )}
                                    </div>
                                    {plan.has_trial && (
                                        <p className="text-green-600 font-bold mt-2">
                                            Après 1 mois gratuit
                                        </p>
                                    )}
                                </div>
                                
                                <ul className="space-y-3 mb-8 py-6 border-t-2 border-b-2 border-gray-100">
                                    {plan.features.map((feature, idx) => (
                                        <li key={idx} className="flex items-start gap-3">
                                            <Check className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                                                plan.id === 'free' ? 'text-gray-400' : 'text-green-500'
                                            }`} />
                                            <span className="text-sm text-muted-foreground">{feature}</span>
                                        </li>
                                    ))}
                                </ul>
                                
                                <Button
                                    onClick={() => handleSubscribe(plan.id)}
                                    disabled={checkoutLoading === plan.id || plan.id === 'free'}
                                    className={`w-full rounded-full py-6 font-bold text-lg border-2 border-foreground ${
                                        plan.id === 'free'
                                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed shadow-none'
                                            : plan.is_popular
                                                ? 'bg-green-500 text-white shadow-pop hover:shadow-pop-hover hover:-translate-y-1 transition-all'
                                                : 'bg-purple-500 text-white shadow-pop hover:shadow-pop-hover hover:-translate-y-1 transition-all'
                                    }`}
                                    data-testid={`subscribe-${plan.id}-btn`}
                                >
                                    {checkoutLoading === plan.id ? (
                                        <>
                                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                                            Chargement...
                                        </>
                                    ) : plan.id === 'free' ? (
                                        'Plan actuel'
                                    ) : (
                                        <>
                                            Commencer l&apos;essai gratuit
                                            <ArrowRight className="w-5 h-5 ml-2" />
                                        </>
                                    )}
                                </Button>
                            </div>
                        ))}
                    </div>
                )}

                {/* Trust Elements */}
                <div className="mt-16 text-center">
                    <div className="inline-flex items-center gap-6 flex-wrap justify-center">
                        <span className="flex items-center gap-2 text-muted-foreground">
                            <Shield className="w-5 h-5 text-green-500" />
                            Paiement sécurisé par Stripe
                        </span>
                        <span className="flex items-center gap-2 text-muted-foreground">
                            <Check className="w-5 h-5 text-green-500" />
                            Annulation à tout moment
                        </span>
                        <span className="flex items-center gap-2 text-muted-foreground">
                            <Gift className="w-5 h-5 text-green-500" />
                            Premier mois 100% gratuit
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SubscriptionPage;
